// src/components/CashEntry/CashEntry.js - Atualizado para suportar colaboradores
import React, { useState, useEffect } from "react";
import { auth, db } from "../../firebase/config";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  limit,
  getDoc
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import MainLayout from "../Layout/MainLayout";
import "./CashEntry.css";
import { useUserContext } from "../../contexts/UserContext";

// Importar serviços refatorados para garantir consistência
import { loadAvailablePeriods } from "../../services/transactionRepository";

const CashEntry = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingEntry, setSavingEntry] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [periodLabel, setPeriodLabel] = useState("");
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Debug State
  const [debugInfo, setDebugInfo] = useState("");

  // Form states
  const [isEditing, setIsEditing] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [entryType, setEntryType] = useState("income"); // "income" or "expense"
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedCategory, setSelectedCategory] = useState("");
  
  // Obter o contexto completo do usuário incluindo perfis ativos
  const { 
    activeAccountType, 
    getAccountSubpath,
    activeBusinessProfileId,
    businessProfiles,
    isCollaborator
  } = useUserContext();
  
  // Categories for expenses
  const [categories, setCategories] = useState({});
  const [flatCategories, setFlatCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Date constraints
  const [dateRange, setDateRange] = useState({
    min: "",
    max: "",
    year: "",
    month: ""
  });

  // Confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);

  // Obter informações do perfil ativo para exibição
  const getActiveProfileInfo = () => {
    if (activeAccountType === 'personal') {
      return { name: 'Pessoal', type: 'personal' };
    } else {
      const activeProfile = businessProfiles.find(p => p.id === activeBusinessProfileId);
      return { 
        name: activeProfile?.profileName || 'Empresarial', 
        type: 'business',
        profileId: activeBusinessProfileId 
      };
    }
  };

  const activeProfileInfo = getActiveProfileInfo();

  // Verify auth state and ensure it's consistent
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        console.log("User authenticated:", currentUser.uid);
        setUser(currentUser);
        checkUserHasCollection(currentUser.uid);
        loadPeriods(currentUser);
        loadUserCategories(currentUser.uid);
      } else {
        console.log("No authenticated user");
        setUser(null);
        setError("Você precisa estar autenticado para acessar esta página.");
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load user categories for expense entries - Atualizada para múltiplos perfis
  const loadUserCategories = async (userId) => {
    try {
      setLoadingCategories(true);
      
      const accountSubpath = getAccountSubpath();
      const isPersonal = activeAccountType === 'personal';
      
      // Verificar se o usuário é um colaborador e obter o ID do proprietário
      let ownerUserId = userId;
      
      // Verificar no documento do usuário se é um colaborador
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists() && userDoc.data().isCollaborator === true) {
        // Verificar informações do workspace
        const invitedBy = userDoc.data().invitedBy;
        const originalInvitation = userDoc.data().originalInvitation || {};
        const profileId = originalInvitation.profileId;
        
        // Verificar se o perfil ativo corresponde ao perfil para o qual foi convidado
        if (activeBusinessProfileId === profileId && invitedBy) {
          ownerUserId = invitedBy; // Usar o ID do proprietário
          console.log(`Colaborador detectado. Carregando categorias do proprietário: ${ownerUserId}`);
        }
      }
      
      let userCategoriesDocRef;
      
      if (isPersonal) {
        // Para conta pessoal, usar a subcoleção "personal/categories"
        userCategoriesDocRef = doc(db, "userCategories", ownerUserId, "personal", "categories");
      } else {
        // Para perfil empresarial, usar a lógica baseada no perfil ativo
        if (accountSubpath === 'business') {
          // Para o perfil principal, verificar primeiro no novo caminho estruturado
          userCategoriesDocRef = doc(db, "userCategories", ownerUserId, "business", "categories");
          
          // Verificar se o documento existe
          const docSnapshot = await getDoc(userCategoriesDocRef);
          
          // Se não existir, tentar o caminho legado (retrocompatibilidade)
          if (!docSnapshot.exists()) {
            console.log(`Documento de categorias não encontrado para perfil business, tentando caminho antigo...`);
            userCategoriesDocRef = doc(db, "userCategories", ownerUserId);
          }
        } else {
          // Para outros perfis empresariais, usar o ID do perfil
          userCategoriesDocRef = doc(db, "userCategories", ownerUserId, accountSubpath, "categories");
        }
      }
      
      const userCategoriesDoc = await getDoc(userCategoriesDocRef);
      
      if (userCategoriesDoc.exists()) {
        const categoriesData = userCategoriesDoc.data();
        
        // Process categories into a more usable format
        if (categoriesData.categories) {
          // Extrair categorias selecionadas organizadas por grupo
          const categoriesByGroup = {};
          const flattenedCategories = [];
          
          Object.keys(categoriesData.categories).forEach(path => {
            if (categoriesData.categories[path]) {
              const parts = path.split('.');
              if (parts.length === 2) {
                const group = parts[0];
                const item = parts[1];
                
                if (!categoriesByGroup[group]) {
                  categoriesByGroup[group] = {
                    items: []
                  };
                }
                
                categoriesByGroup[group].items.push(item);
                
                // Adicionar ao array de categorias planas
                flattenedCategories.push({
                  label: item,
                  value: path,
                  group: group
                });
              }
            }
          });
          
          // Ordenar alfabeticamente
          flattenedCategories.sort((a, b) => a.label.localeCompare(b.label));
          
          setCategories(categoriesByGroup);
          setFlatCategories(flattenedCategories);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
      setError("Não foi possível carregar suas categorias. Por favor, tente novamente.");
    } finally {
      setLoadingCategories(false);
    }
  };

  // Check if user collection exists - Atualizada para múltiplos perfis
  const checkUserHasCollection = async (userId) => {
    try {
      const accountSubpath = getAccountSubpath();
      
      // Verificar se o usuário é um colaborador e obter o ID do proprietário
      let ownerUserId = userId;
      
      // Verificar no documento do usuário se é um colaborador
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists() && userDoc.data().isCollaborator === true) {
        // Verificar informações do workspace
        const invitedBy = userDoc.data().invitedBy;
        const originalInvitation = userDoc.data().originalInvitation || {};
        const profileId = originalInvitation.profileId;
        
        // Verificar se o perfil ativo corresponde ao perfil para o qual foi convidado
        if (activeBusinessProfileId === profileId && invitedBy) {
          ownerUserId = invitedBy; // Usar o ID do proprietário
          console.log(`Colaborador detectado. Verificando acesso: ${ownerUserId}`);
        }
      }
      
      // Check if the user has documents in the cashEntries collection
      const testQuery = query(
        collection(db, "cashEntries", ownerUserId, accountSubpath),
        limit(1)
      );

      await getDocs(testQuery); // Just testing if we can access this
    } catch (error) {
      console.error("Erro ao verificar permissões:", error);
      setDebugInfo(`Erro de permissão: ${error.message}`);

      // Try to create a test document to see if writing is allowed
      try {
        const testDoc = await addDoc(collection(db, "userTest"), {
          userId,
          test: true,
          timestamp: serverTimestamp()
        });

        // If we get here, we can write but not read from cashEntries
        setDebugInfo("Permissão de escrita: OK, mas não há permissão de leitura em cashEntries");

        // Clean up test document
        await deleteDoc(doc(db, "userTest", testDoc.id));
      } catch (writeError) {
        console.error("Erro ao testar permissão de escrita:", writeError);
        setDebugInfo(`Sem permissão de leitura nem escrita: ${writeError.message}`);
      }
    }
  };

  // Load available periods - Atualizada para usar serviço refatorado
  const loadPeriods = async (currentUser) => {
    try {
      if (!currentUser) return;

      console.log("Loading periods for user:", currentUser.uid);

      // Usar o serviço refatorado para carregar períodos
      const availablePeriods = await loadAvailablePeriods();

      // If no periods found, create a default one
      if (availablePeriods.length === 0) {
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');

        const defaultPeriod = {
          value: `${year}-${month}`,
          label: `${getMonthName(month)} de ${year}`
        };

        availablePeriods.push(defaultPeriod);
        console.log("Created default period:", defaultPeriod);
      }

      setPeriods(availablePeriods);

      // Check for saved period in localStorage
      let savedPeriod = localStorage.getItem('selectedPeriod');
      let periodToUse;
      let periodLabelToUse;

      if (savedPeriod && availablePeriods.find(p => p.value === savedPeriod)) {
        periodToUse = savedPeriod;
        const savedPeriodObj = availablePeriods.find(p => p.value === savedPeriod);
        periodLabelToUse = savedPeriodObj.label;
      } else if (availablePeriods.length > 0) {
        periodToUse = availablePeriods[0].value;
        periodLabelToUse = availablePeriods[0].label;
        localStorage.setItem('selectedPeriod', periodToUse);
      }

      if (periodToUse) {
        setSelectedPeriod(periodToUse);
        setPeriodLabel(periodLabelToUse);
        updateDateConstraints(periodToUse);
      }
    } catch (error) {
      console.error("Erro ao carregar períodos:", error);
      setError(`Não foi possível carregar os períodos: ${error.message}`);

      // Create a default period as fallback
      createDefaultPeriod();
    }
  };

  // Create a default period if none exists
  const createDefaultPeriod = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');

    const defaultPeriod = {
      value: `${year}-${month}`,
      label: `${getMonthName(month)} de ${year}`
    };

    setPeriods([defaultPeriod]);
    setSelectedPeriod(defaultPeriod.value);
    setPeriodLabel(defaultPeriod.label);
    updateDateConstraints(defaultPeriod.value);

    console.log("Set default period:", defaultPeriod);
  };

  // Get month name from number
  const getMonthName = (monthNum) => {
    const months = [
      "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
      "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];
    return months[parseInt(monthNum) - 1];
  };

  // Update date constraints when period changes
  const updateDateConstraints = (period) => {
    if (!period) return;

    const [year, month] = period.split("-");
    const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();

    const minDate = `${year}-${month}-01`;
    const maxDate = `${year}-${month}-${lastDay}`;

    setDateRange({
      min: minDate,
      max: maxDate,
      year,
      month
    });

    // Set default date to today if in range, otherwise first day of month
    const today = new Date().toISOString().split('T')[0];
    if (today >= minDate && today <= maxDate) {
      setEntryDate(today);
    } else {
      setEntryDate(minDate);
    }
  };

  // Load cash entries when period changes
  useEffect(() => {
    if (selectedPeriod && user) {
      loadCashEntries();
    }
  }, [selectedPeriod, user, activeAccountType, activeBusinessProfileId]); // Adicionar activeBusinessProfileId

  // Load cash entries for selected period - Atualizada para múltiplos perfis e CORRIGIDA para evitar índices
  const loadCashEntries = async () => {
    try {
      setLoading(true);

      if (!auth.currentUser || !selectedPeriod) {
        console.log("Cannot load entries: Missing user or period");
        return;
      }

      console.log("Loading entries for period:", selectedPeriod);

      const accountSubpath = getAccountSubpath();
      
      // MODIFICADO: Verificar se o usuário é um colaborador e obter o ID do proprietário
      let userId = auth.currentUser.uid;
      
      // Verificar no documento do usuário se é um colaborador
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists() && userDoc.data().isCollaborator === true) {
        // Verificar informações do workspace
        const invitedBy = userDoc.data().invitedBy;
        const originalInvitation = userDoc.data().originalInvitation || {};
        const profileId = originalInvitation.profileId;
        
        // Verificar se o perfil ativo corresponde ao perfil para o qual foi convidado
        if (activeBusinessProfileId === profileId && invitedBy) {
          userId = invitedBy; // Usar o ID do proprietário
          console.log(`Colaborador detectado. Carregando entradas do proprietário: ${userId}`);
        }
      }
      
      // First, check if collection exists by getting a single document
      try {
        const testQuery = query(
          collection(db, "cashEntries", userId, accountSubpath),
          limit(1)
        );

        const testSnapshot = await getDocs(testQuery);

        // If collection doesn't exist or is empty, show empty state
        if (testSnapshot.empty) {
          console.log("No entries found, possibly new collection");
          setEntries([]);
          setLoading(false);
          return;
        }
      } catch (testError) {
        console.error("Error testing cashEntries collection:", testError);
        // If we get permission error, it might be because collection doesn't exist yet
        setEntries([]);
        setLoading(false);
        return;
      }

      // CORRIGIDO: Query simplificada sem orderBy para evitar índice composto dinâmico
      const entriesQuery = query(
        collection(db, "cashEntries", userId, accountSubpath),
        where("period", "==", selectedPeriod)
        // REMOVIDO: orderBy("transactionDate", "desc") <- isto causava a necessidade de índice composto
      );

      const entriesSnapshot = await getDocs(entriesQuery);
      const entriesList = [];

      entriesSnapshot.forEach(doc => {
        entriesList.push({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          transactionDate: doc.data().transactionDate || doc.data().createdAt?.toDate().toISOString().split('T')[0],
          // Adicionar flag para indicar se foi criado por um colaborador
          isCollaboratorEntry: doc.data().isCollaboratorEntry || false,
          createdByName: doc.data().createdByName || null
        });
      });

      // ADICIONADO: Ordenação no lado do cliente (JavaScript) para substituir o orderBy removido
      entriesList.sort((a, b) => {
        // Ordenar por transactionDate em ordem descendente (mais recente primeiro)
        const dateA = new Date(a.transactionDate);
        const dateB = new Date(b.transactionDate);
        return dateB - dateA;
      });

      console.log(`Loaded ${entriesList.length} entries`);
      setEntries(entriesList);
    } catch (error) {
      console.error("Erro ao carregar entradas de dinheiro:", error);

      // More detailed error handling
      if (error.code === "permission-denied") {
        setError("Permissão negada ao acessar as entradas de dinheiro. Verifique as regras do Firestore.");
      } else {
        setError(`Erro ao carregar entradas: ${error.message}`);
      }

      // Set empty entries to avoid breaking UI
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  // Change period handler
  const handlePeriodChange = (event) => {
    const period = event.target.value;
    setSelectedPeriod(period);

    const selectedPeriodObj = periods.find(p => p.value === period);
    if (selectedPeriodObj) {
      setPeriodLabel(selectedPeriodObj.label);
    }

    localStorage.setItem('selectedPeriod', period);
    resetForm();
    updateDateConstraints(period);
  };

  // Reset form
  const resetForm = () => {
    setAmount("");
    setDescription("");
    setIsEditing(false);
    setEditingEntryId(null);
    setEntryType("income");
    setSelectedCategory("");

    // Reset date to current date or first day of month if current date is outside range
    const today = new Date().toISOString().split('T')[0];
    if (today >= dateRange.min && today <= dateRange.max) {
      setEntryDate(today);
    } else {
      setEntryDate(dateRange.min);
    }
  };

  // Handle date input
  const handleDateChange = (e) => {
    setEntryDate(e.target.value);
  };

  // Handle entry type change
  const handleEntryTypeChange = (e) => {
    setEntryType(e.target.value);
    // Reset category selection when changing type
    if (e.target.value === "income") {
      setSelectedCategory("");
    }
  };

  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "-";

    if (typeof dateString === 'string') {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }

    return new Intl.DateTimeFormat('pt-BR').format(dateString);
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Submit form handler - Atualizada para múltiplos perfis e colaboradores
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSavingEntry(true);
      setError("");

      if (!auth.currentUser) {
        throw new Error("Usuário não autenticado");
      }

      if (!selectedPeriod) {
        throw new Error("Selecione um período");
      }

      if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
        throw new Error("Digite um valor válido maior que zero");
      }

      if (entryType === "expense" && !selectedCategory) {
        throw new Error("Selecione uma categoria para a saída de dinheiro");
      }

      if (!entryDate) {
        throw new Error("Selecione a data da entrada");
      }

      // Validate date is within period range
      if (entryDate < dateRange.min || entryDate > dateRange.max) {
        throw new Error(`A data deve estar dentro do período ${periodLabel}`);
      }

      // Parse the amount and apply sign based on type
      let numericAmount = parseFloat(amount);
      // For expenses, make the amount negative
      if (entryType === "expense") {
        numericAmount = -numericAmount;
      }

      // MODIFICADO: Verificar se o usuário é um colaborador e obter o ID do proprietário
      let userId = auth.currentUser.uid;
      let workspaceOwner = null;
      
      // Verificar no documento do usuário se é um colaborador
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists() && userDoc.data().isCollaborator === true) {
        // Verificar informações do workspace
        const invitedBy = userDoc.data().invitedBy;
        const originalInvitation = userDoc.data().originalInvitation || {};
        const profileId = originalInvitation.profileId;
        
        // Verificar se o perfil ativo corresponde ao perfil para o qual foi convidado
        if (activeBusinessProfileId === profileId && invitedBy) {
          userId = invitedBy; // Usar o ID do proprietário
          workspaceOwner = invitedBy;
          console.log(`Colaborador detectado. Usando ID do proprietário: ${userId}`);
        }
      }

      // Get category information for expenses
      let categoryInfo = {};
      if (entryType === "expense" && selectedCategory) {
        const categoryObj = flatCategories.find(cat => cat.value === selectedCategory);
        if (categoryObj) {
          categoryInfo = {
            category: categoryObj.label,
            categoryPath: selectedCategory,
            groupName: categoryObj.group
          };
        }
      }

      console.log("Saving entry:", {
        amount: numericAmount,
        date: entryDate,
        period: selectedPeriod,
        type: entryType,
        userId,
        activeAccountType,
        activeBusinessProfileId,
        accountSubpath: getAccountSubpath(),
        ...(entryType === "expense" ? { categoryInfo } : {})
      });

      const accountSubpath = getAccountSubpath();

      if (isEditing && editingEntryId) {
        // Usar o caminho correto para o documento com o ID do proprietário se for colaborador
        const docRef = doc(db, "cashEntries", userId, accountSubpath, editingEntryId);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          throw new Error("A entrada que você está tentando editar não existe mais.");
        }

        // Update existing entry
        await updateDoc(docRef, {
          amount: numericAmount,
          description: description.trim(),
          transactionDate: entryDate,
          entryType: entryType,
          ...(entryType === "expense" ? {
            category: categoryInfo.category || null,
            categoryPath: categoryInfo.categoryPath || null,
            groupName: categoryInfo.groupName || null
          } : {
            category: "Dinheiro",
            categoryPath: activeAccountType === 'personal' ? "💸 Receitas.Dinheiro" : "RECEITA.Dinheiro",
            groupName: activeAccountType === 'personal' ? "💸 Receitas" : "RECEITA"
          }),
          updatedAt: serverTimestamp(),
          // Adicionar informação do colaborador se aplicável
          ...(workspaceOwner ? {
            lastEditedBy: auth.currentUser.uid,
            lastEditedByName: auth.currentUser.displayName || "Colaborador",
            isCollaboratorEdit: true
          } : {})
        });

        setSuccess(entryType === "income" ? 
          "Entrada atualizada com sucesso!" : 
          "Saída atualizada com sucesso!");
      } else {
        // Add new entry
        const entryData = {
          period: selectedPeriod,
          periodLabel,
          amount: numericAmount,
          description: description.trim(),
          transactionDate: entryDate,
          entryType: entryType,
          ...(entryType === "expense" ? {
            category: categoryInfo.category || null,
            categoryPath: categoryInfo.categoryPath || null,
            groupName: categoryInfo.groupName || null
          } : {
            category: "Dinheiro",
            categoryPath: activeAccountType === 'personal' ? "💸 Receitas.Dinheiro" : "RECEITA.Dinheiro",
            groupName: activeAccountType === 'personal' ? "💸 Receitas" : "RECEITA"
          }),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          // Adicionar informação do colaborador se aplicável
          ...(workspaceOwner ? {
            createdBy: auth.currentUser.uid,
            createdByName: auth.currentUser.displayName || "Colaborador",
            isCollaboratorEntry: true
          } : {})
        };

        // Usar a subcoleção correta com o ID do proprietário se for colaborador
        await addDoc(collection(db, "cashEntries", userId, accountSubpath), entryData);
        setSuccess(entryType === "income" ? 
          "Entrada de dinheiro adicionada com sucesso!" : 
          "Saída de dinheiro adicionada com sucesso!");
      }

      await loadCashEntries();
      resetForm();

      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Erro ao salvar entrada:", error);

      // Detailed error handling
      if (error.code === "permission-denied") {
        setError("Permissão negada ao salvar a entrada. Verifique as regras do Firestore.");
      } else {
        setError(error.message || "Não foi possível salvar a operação de dinheiro.");
      }

      setTimeout(() => setError(""), 5000);
    } finally {
      setSavingEntry(false);
    }
  };

  // Edit entry handler
  const handleEditEntry = (entry) => {
    setAmount(Math.abs(entry.amount).toString());
    setDescription(entry.description || "");
    setEntryDate(entry.transactionDate || dateRange.min);
    setEntryType(entry.entryType || (entry.amount >= 0 ? "income" : "expense"));
    
    if (entry.categoryPath && entry.entryType === "expense") {
      setSelectedCategory(entry.categoryPath);
    } else {
      setSelectedCategory("");
    }
    
    setIsEditing(true);
    setEditingEntryId(entry.id);

    // Scroll to form
    document.querySelector('.cash-entry-form-card')?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  // Confirm delete handler
  const confirmDelete = (entry) => {
    setEntryToDelete(entry);
    setShowDeleteModal(true);
  };

  // Delete entry handler - Atualizada para múltiplos perfis e colaboradores
  const handleDeleteEntry = async () => {
    if (!entryToDelete) return;

    try {
      setShowDeleteModal(false);
      setLoading(true);

      const accountSubpath = getAccountSubpath();
      
      // MODIFICADO: Verificar se o usuário é um colaborador e obter o ID do proprietário
      let userId = auth.currentUser.uid;
      
      // Verificar no documento do usuário se é um colaborador
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists() && userDoc.data().isCollaborator === true) {
        // Verificar informações do workspace
        const invitedBy = userDoc.data().invitedBy;
        const originalInvitation = userDoc.data().originalInvitation || {};
        const profileId = originalInvitation.profileId;
        
        // Verificar se o perfil ativo corresponde ao perfil para o qual foi convidado
        if (activeBusinessProfileId === profileId && invitedBy) {
          userId = invitedBy; // Usar o ID do proprietário
          console.log(`Colaborador detectado. Excluindo entrada do proprietário: ${userId}`);
        }
      }
      
      // Usar o caminho correto para o documento com o ID do proprietário se for colaborador
      const docRef = doc(db, "cashEntries", userId, accountSubpath, entryToDelete.id);

      // Check if document exists first
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error("A entrada que você está tentando excluir não existe mais.");
      }

      await deleteDoc(docRef);

      setSuccess("Registro removido com sucesso!");
      await loadCashEntries();

      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Erro ao excluir entrada:", error);

      if (error.code === "permission-denied") {
        setError("Permissão negada ao excluir a entrada. Verifique as regras do Firestore.");
      } else {
        setError("Não foi possível excluir a entrada: " + error.message);
      }

      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
      setEntryToDelete(null);
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    const income = entries
      .filter(entry => entry.amount >= 0)
      .reduce((total, entry) => total + (entry.amount || 0), 0);
      
    const expense = entries
      .filter(entry => entry.amount < 0)
      .reduce((total, entry) => total + (entry.amount || 0), 0);
      
    return {
      income,
      expense,
      balance: income + expense
    };
  };

  // Atualizar recarregamento quando trocar de perfil
  useEffect(() => {
    if (user) {
      loadUserCategories(user.uid);
      if (selectedPeriod) {
        loadCashEntries();
      }
    }
  }, [activeAccountType, activeBusinessProfileId]);

  // Check if special period (Feb 2025)
  const isFebruary2025 = dateRange.year === "2025" && dateRange.month === "02";

  // Calculate totals
  const totals = calculateTotals();

  return (
    <MainLayout userName={user?.displayName || "Usuário"}>
      <div className="cash-entry-container">
        <div className="cash-entry-header">
          <h1>
            Movimentação de Dinheiro - {activeProfileInfo.name}
            {activeProfileInfo.type === 'business' && activeProfileInfo.profileId && 
             activeProfileInfo.profileId !== 'business' && (
              <span className="profile-indicator"> (Perfil: {activeProfileInfo.profileId})</span>
            )}
            {isCollaborator && (
              <span className="collaborator-indicator"> (Modo Colaborador)</span>
            )}
          </h1>

          <div className="period-selector">
            <label htmlFor="period-select">Período:</label>
            <select
              id="period-select"
              value={selectedPeriod}
              onChange={handlePeriodChange}
              disabled={loading || savingEntry}
              className="period-select"
            >
              {periods.length === 0 ? (
                <option value="">Nenhum período disponível</option>
              ) : (
                periods.map((period) => (
                  <option key={period.value} value={period.value}>
                    {period.label}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        {error && (
          <div className="notification error-notification">
            <span className="notification-message">{error}</span>
            <button className="notification-close" onClick={() => setError("")}>×</button>
          </div>
        )}

        {success && (
          <div className="notification success-notification">
            <span className="notification-message">{success}</span>
            <button className="notification-close" onClick={() => setSuccess("")}>×</button>
          </div>
        )}

        {debugInfo && (
          <div className="notification debug-notification">
            <span className="notification-message">{debugInfo}</span>
            <button className="notification-close" onClick={() => setDebugInfo("")}>×</button>
          </div>
        )}

        {loading && !selectedPeriod ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Carregando dados...</p>
          </div>
        ) : (
          <>
            {/* Cash Entry Form */}
            <div className="cash-entry-form-card">
              <h2>{isEditing ? 
                (entryType === "income" ? "Editar Entrada de Dinheiro" : "Editar Saída de Dinheiro") : 
                "Nova Movimentação de Dinheiro"}
              </h2>

              <form onSubmit={handleSubmit} className="cash-entry-form">
                {/* Tipo de movimento (entrada/saída) */}
                <div className="form-row">
                  <div className="form-field">
                    <label>Tipo de Movimentação<span className="required">*</span></label>
                    <div className="entry-type-selector">
                      <label className={`entry-type-option ${entryType === "income" ? "selected" : ""}`}>
                        <input
                          type="radio"
                          name="entryType"
                          value="income"
                          checked={entryType === "income"}
                          onChange={handleEntryTypeChange}
                          disabled={savingEntry}
                        />
                        <span className="entry-type-label">📥 Entrada</span>
                      </label>
                      <label className={`entry-type-option ${entryType === "expense" ? "selected" : ""}`}>
                        <input
                          type="radio"
                          name="entryType"
                          value="expense"
                          checked={entryType === "expense"}
                          onChange={handleEntryTypeChange}
                          disabled={savingEntry}
                        />
                        <span className="entry-type-label">📤 Saída</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="amount">
                      Valor (R$)<span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      id="amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Ex: 100.00"
                      min="0.01"
                      step="0.01"
                      required
                      disabled={savingEntry}
                      className="form-input"
                    />
                  </div>

                  <div className="form-field date-field-container">
                    <label htmlFor="entry-date">
                      Data<span className="required">*</span>
                    </label>
                    {isFebruary2025 ? (
                      // Special handling for February 2025
                      <div className="date-input-container">
                        <select
                          id="day-select"
                          value={entryDate.split('-')[2]}
                          onChange={(e) => setEntryDate(`2025-02-${e.target.value}`)}
                          disabled={savingEntry}
                          className="day-only-select"
                        >
                          {Array.from({ length: 29 }, (_, i) => {
                            const day = (i + 1).toString().padStart(2, '0');
                            return (
                              <option key={day} value={day}>
                                {day}
                              </option>
                            );
                          })}
                        </select>
                        <span className="date-separator">/</span>
                        <div className="static-date-part">Fevereiro</div>
                        <span className="date-separator">/</span>
                        <div className="static-date-part">2025</div>
                      </div>
                    ) : (
                      <input
                        type="date"
                        id="entry-date"
                        value={entryDate}
                        onChange={handleDateChange}
                        min={dateRange.min}
                        max={dateRange.max}
                        required
                        disabled={savingEntry}
                        className="form-input date-input"
                      />
                    )}
                    <span className="input-help-text">
                      Período: {formatDisplayDate(dateRange.min)} a {formatDisplayDate(dateRange.max)}
                    </span>
                  </div>
                </div>

                {/* Categoria (apenas para saídas) */}
                {entryType === "expense" && (
                  <div className="form-field">
                    <label htmlFor="category-select">
                      Categoria<span className="required">*</span>
                    </label>
                    <select
                      id="category-select"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="form-input"
                      required
                      disabled={savingEntry || loadingCategories}
                    >
                      <option value="">Selecione uma categoria</option>
                      {loadingCategories ? (
                        <option value="" disabled>Carregando categorias...</option>
                      ) : (
                        flatCategories.map((category, index) => (
                          <option key={`cat-${index}`} value={category.value}>
                            {category.label}
                          </option>
                        ))
                      )}
                    </select>
                    <span className="input-help-text">
                      Selecione a categoria para classificar esta saída de dinheiro
                    </span>
                  </div>
                )}

                <div className="form-field">
                  <label htmlFor="description">Descrição</label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descrição opcional"
                    rows="2"
                    disabled={savingEntry}
                    className="form-input description-input"
                  ></textarea>
                </div>

                <div className="form-actions">
                  {isEditing && (
                    <button
                      type="button"
                      className="cancel-button"
                      onClick={resetForm}
                      disabled={savingEntry}
                    >
                      Cancelar
                    </button>
                  )}

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={savingEntry || !amount || (entryType === "expense" && !selectedCategory)}
                  >
                    {savingEntry ? (
                      <>
                        <span className="button-spinner"></span>
                        <span>Salvando...</span>
                      </>
                    ) : (
                      isEditing ? 
                        (entryType === "income" ? "Atualizar Entrada" : "Atualizar Saída") : 
                        (entryType === "income" ? "Adicionar Entrada" : "Adicionar Saída")
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Entries List */}
            <div className="entries-list-card">
              <div className="entries-header">
                <h2>Movimentações em {periodLabel}</h2>
                <div className="totals-container">
                  <div className="entries-total income-total">
                    <span className="total-label">Entradas:</span>
                    <span className="total-value">{formatCurrency(totals.income)}</span>
                  </div>
                  <div className="entries-total expense-total">
                    <span className="total-label">Saídas:</span>
                    <span className="total-value">{formatCurrency(totals.expense)}</span>
                  </div>
                  <div className="entries-total balance-total">
                    <span className="total-label">Saldo:</span>
                    <span className={`total-value ${totals.balance >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(totals.balance)}
                    </span>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Carregando entradas...</p>
                </div>
              ) : entries.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">💵</div>
                  <p className="empty-message">Nenhuma movimentação de dinheiro registrada para este período.</p>
                  <p className="empty-help">Use o formulário acima para adicionar uma nova movimentação.</p>
                </div>
              ) : (
                <div className="entries-table-container">
                  <table className="entries-table">
                    <thead>
                      <tr>
                        <th className="date-column">Data</th>
                        <th className="amount-column">Valor</th>
                        <th className="category-column">Categoria</th>
                        <th className="description-column">Descrição</th>
                        <th className="actions-column">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => (
                        <tr key={entry.id} className={`${entry.amount >= 0 ? 'income-row' : 'expense-row'} ${entry.isCollaboratorEntry ? 'collaborator-entry' : ''}`}>
                          <td>{formatDisplayDate(entry.transactionDate)}</td>
                          <td className={`amount-cell ${entry.amount >= 0 ? 'income-amount' : 'expense-amount'}`}>
                            {formatCurrency(entry.amount)}
                          </td>
                          <td className="category-cell">
                            {entry.category ? (
                              <span className="category-badge">{entry.category}</span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="description-cell">
                            {entry.description || "-"}
                            {entry.isCollaboratorEntry && entry.createdByName && (
                              <div className="collaborator-tag">
                                Adicionado por: {entry.createdByName}
                              </div>
                            )}
                          </td>
                          <td className="actions-cell">
                            <button
                              className="action-button edit-button"
                              onClick={() => handleEditEntry(entry)}
                              title="Editar"
                              disabled={savingEntry}
                            >
                              <span className="action-icon">✎</span>
                              <span className="action-text">Editar</span>
                            </button>
                            <button
                              className="action-button delete-button"
                              onClick={() => confirmDelete(entry)}
                              title="Excluir"
                              disabled={savingEntry}
                            >
                              <span className="action-icon">×</span>
                              <span className="action-text">Excluir</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal-overlay">
            <div className="modal-container">
              <div className="modal-header">
                <h3>Confirmar Exclusão</h3>
                <button
                  className="modal-close"
                  onClick={() => setShowDeleteModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-content">
                <p className="modal-message">Tem certeza que deseja excluir esta movimentação de dinheiro?</p>
                <div className="entry-details">
                  <div className="entry-detail">
                    <span className="detail-label">Valor:</span>
                    <span className="detail-value">{formatCurrency(entryToDelete?.amount || 0)}</span>
                  </div>
                  <div className="entry-detail">
                    <span className="detail-label">Data:</span>
                    <span className="detail-value">{formatDisplayDate(entryToDelete?.transactionDate)}</span>
                  </div>
                  {entryToDelete?.category && (
                    <div className="entry-detail">
                      <span className="detail-label">Categoria:</span>
                      <span className="detail-value">{entryToDelete.category}</span>
                    </div>
                  )}
                  {entryToDelete?.description && (
                    <div className="entry-detail">
                      <span className="detail-label">Descrição:</span>
                      <span className="detail-value">{entryToDelete.description}</span>
                    </div>
                  )}
                </div>
                <p className="warning-text">Esta ação não pode ser desfeita.</p>
              </div>
              <div className="modal-footer">
                <button
                  className="cancel-button"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancelar
                </button>
                <button
                  className="delete-button"
                  onClick={handleDeleteEntry}
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Adicionar estilo CSS para itens colaborativos */}
      <style>{`
        .collaborator-entry {
          background-color: rgba(0, 123, 255, 0.05);
        }
        
        .collaborator-tag {
          font-size: 0.8em;
          color: #0066cc;
          font-style: italic;
          margin-top: 4px;
        }
        
        .collaborator-indicator {
          color: #0066cc;
          font-size: 0.8em;
          font-weight: normal;
        }
      `}</style>
    </MainLayout>
  );
};

export default CashEntry;